import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Video, Languages, Shuffle, Sparkles } from 'lucide-react';

// Animated icon components for each feature
function VideoAnimation() {
  return (
    <div className="relative w-12 h-12 bg-[#11B981]/10 rounded-lg flex items-center justify-center overflow-hidden">
      <Video className="w-6 h-6 text-[#11B981] relative z-10" />
      {/* Recording pulse */}
      <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
      {/* Signal waves */}
      <span className="absolute inset-0 border-2 border-[#11B981]/30 rounded-lg animate-ping" style={{ animationDuration: '2s' }} />
    </div>
  );
}

function TranslationAnimation() {
  const [langIndex, setLangIndex] = useState(0);
  const languages = ['EN', 'ES', 'FR', 'DE', 'JA', 'KO'];

  useEffect(() => {
    const interval = setInterval(() => {
      setLangIndex((prev) => (prev + 1) % languages.length);
    }, 1500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative w-12 h-12 bg-[#11B981]/10 rounded-lg flex items-center justify-center overflow-hidden">
      <Languages className="w-6 h-6 text-[#11B981]" />
      {/* Cycling language badge */}
      <span
        className="absolute -bottom-0.5 -right-0.5 bg-[#11B981] text-[8px] font-bold text-white px-1 rounded transition-all duration-300"
        key={langIndex}
        style={{ animation: 'fadeInUp 0.3s ease-out' }}
      >
        {languages[langIndex]}
      </span>
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

function MatchingAnimation() {
  return (
    <div className="relative w-12 h-12 bg-[#11B981]/10 rounded-lg flex items-center justify-center overflow-hidden">
      <Shuffle className="w-6 h-6 text-[#11B981] animate-shuffle" />
      {/* Connection dots */}
      <span className="absolute top-1 left-1 w-1.5 h-1.5 bg-[#11B981] rounded-full animate-bounce" style={{ animationDelay: '0ms', animationDuration: '1s' }} />
      <span className="absolute bottom-1 right-1 w-1.5 h-1.5 bg-[#11B981] rounded-full animate-bounce" style={{ animationDelay: '500ms', animationDuration: '1s' }} />
      <style>{`
        @keyframes shuffle {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(-10deg); }
          75% { transform: rotate(10deg); }
        }
        .animate-shuffle {
          animation: shuffle 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

function FiltersAnimation() {
  const [colorIndex, setColorIndex] = useState(0);
  const colors = ['#11B981', '#8B5CF6', '#F59E0B', '#EC4899', '#3B82F6'];

  useEffect(() => {
    const interval = setInterval(() => {
      setColorIndex((prev) => (prev + 1) % colors.length);
    }, 1200);
    return () => clearInterval(interval);
  }, []);

  return (
    <div
      className="relative w-12 h-12 rounded-lg flex items-center justify-center overflow-hidden transition-all duration-500"
      style={{ backgroundColor: `${colors[colorIndex]}15` }}
    >
      <Sparkles
        className="w-6 h-6 transition-colors duration-500"
        style={{ color: colors[colorIndex] }}
      />
      {/* Sparkle particles */}
      <span
        className="absolute top-1 right-2 w-1 h-1 rounded-full animate-ping"
        style={{ backgroundColor: colors[colorIndex], animationDuration: '1.5s' }}
      />
      <span
        className="absolute bottom-2 left-1 w-1 h-1 rounded-full animate-ping"
        style={{ backgroundColor: colors[colorIndex], animationDuration: '1.5s', animationDelay: '0.5s' }}
      />
    </div>
  );
}

const features = [
  {
    id: 'video',
    icon: VideoAnimation,
    title: 'HD Video Chat',
    description: 'Crystal-clear 720p video quality for face-to-face conversations'
  },
  {
    id: 'translation',
    icon: TranslationAnimation,
    title: 'Real-Time Translation',
    description: 'Chat in 38+ languages with automatic message translation'
  },
  {
    id: 'matching',
    icon: MatchingAnimation,
    title: 'Smart Matching',
    description: 'Meet people from 180+ countries with intelligent pairing'
  },
  {
    id: 'filters',
    icon: FiltersAnimation,
    title: 'Video Filters',
    description: '9+ filters to express yourself and enhance your video'
  }
];

function FeatureCard({ feature }: { feature: typeof features[0] }) {
  const IconComponent = feature.icon;

  return (
    <Card className="p-6 bg-[#1A2332] border-gray-700 hover:border-[#11B981] transition-colors">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <IconComponent />
        </div>
        <div>
          <h3 className="text-xl font-semibold text-white mb-2">
            {feature.title}
          </h3>
          <p className="text-gray-400">
            {feature.description}
          </p>
        </div>
      </div>
    </Card>
  );
}

export function FeaturesGrid() {
  return (
    <section className="py-20 px-4">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
          Everything You Need to Connect
        </h2>
        <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
          Powerful features designed for seamless global communication
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {features.map((feature) => (
            <FeatureCard key={feature.id} feature={feature} />
          ))}
        </div>
      </div>
    </section>
  );
}
